import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import Modal from 'tailwind-components/Modal/Modal';
import Button from 'tailwind-components/Button/Button';
import CarrierDisclaimer from 'tailwind-components/CarrierDisclaimer/CarrierDisclaimer';
import Separator from 'tailwind-components/Separator/Separator';
import {
  DISCLAIMER_COLORS,
  DISCLAIMER_ICONS
} from 'helpers/factory/carrierFactory';

function CarrierRecommendationRow({ carrierName, states = [] }) {
  const hasStates = states.length > 0;

  return (
    <div className='rounded-lg border border-grey-lighter bg-grey-background/40 px-3 py-2.5'>
      <p className='m-0 text-sm font-semibold text-gray-900'>{carrierName}</p>
      {hasStates ? (
        <ul className='m-0 mt-2 flex list-none flex-wrap gap-1.5 p-0'>
          {states.map(state => (
            <li
              key={state}
              className='rounded-full border border-grey-lighter bg-white px-2.5 py-1 text-xs font-medium text-gray-800'
            >
              {state}
            </li>
          ))}
        </ul>
      ) : (
        <p className='m-0 mt-1 text-xs text-grey'>
          No state-specific licensing required for this carrier.
        </p>
      )}
    </div>
  );
}

CarrierRecommendationRow.propTypes = {
  carrierName: propTypes.string.isRequired,
  states: propTypes.arrayOf(propTypes.string)
};

function SectionCarrierList({ carriers }) {
  if (!carriers?.length) return null;

  return (
    <div className='flex flex-col gap-2'>
      {carriers.map(carrier => (
        <CarrierRecommendationRow
          key={carrier.name}
          carrierName={carrier.name}
          states={carrier.untappedStates ?? []}
        />
      ))}
    </div>
  );
}

SectionCarrierList.propTypes = {
  carriers: propTypes.arrayOf(propTypes.object)
};

export default function UntappedSuggestSummaryModal({
  active,
  agentName,
  agentEmail = '',
  groupedCarriers = [],
  confirmLoading = false,
  onClose,
  onConfirm
}) {
  const hasEmail = Boolean(String(agentEmail || '').trim());
  const totalCarriers = groupedCarriers.reduce((count, section) => {
    const main = section.carriersWithoutSubgroup?.length ?? 0;
    const sub =
      section.subgroups?.reduce((n, sg) => n + (sg.carriers?.length ?? 0), 0) ??
      0;
    return count + main + sub;
  }, 0);

  const carrierLabel =
    totalCarriers === 1 ? 'carrier recommendation' : 'carrier recommendations';

  const disclaimer = useMemo(() => {
    if (!hasEmail) {
      return {
        text: `<b>${agentName}</b> has no email on file. Add an email in Zoho before sending carrier suggestions.`,
        color: DISCLAIMER_COLORS.RED,
        icon: DISCLAIMER_ICONS.INFO
      };
    }
    return {
      text: `Review suggestions for <b>${agentName}</b> (no active appointments and no open requests).<br /><br />Confirming sends an email with <b>${totalCarriers}</b> ${carrierLabel} to <b>${agentEmail}</b> based on this agent&apos;s licenses and your agency&apos;s active carriers.<br /><br /> The agent can open each carrier from the <b>Agent Portal</b> via that email to start or complete appointment requests.`,
      color: DISCLAIMER_COLORS.BLUE,
      icon: DISCLAIMER_ICONS.INFO
    };
  }, [hasEmail, agentName, agentEmail, totalCarriers, carrierLabel]);

  const footer = (
    <div className='flex w-full justify-between gap-2'>
      <Button
        variant='danger'
        outlined
        fill
        size='small'
        text='Cancel'
        onClick={onClose}
        isDisabled={confirmLoading}
      />
      <Button
        fill
        variant='primary'
        size='small'
        text='Confirm & suggest'
        onClick={onConfirm}
        isLoading={confirmLoading}
        isDisabled={confirmLoading || !hasEmail}
      />
    </div>
  );

  return (
    <Modal
      active={active}
      closeModal={onClose}
      title='Suggest to agent'
      size='large'
      showScrollbar
      footer={footer}
    >
      <div className='flex flex-col gap-4'>
        <CarrierDisclaimer disclaimer={disclaimer} />

        <div className='flex flex-col gap-5'>
          {groupedCarriers.map(
            ({ parentGroupName, carriersWithoutSubgroup, subgroups }) => (
              <section key={parentGroupName} className='flex flex-col gap-3'>
                <h4 className='m-0 text-left text-base font-semibold text-gray-900'>
                  {parentGroupName}
                </h4>

                <SectionCarrierList carriers={carriersWithoutSubgroup} />

                {subgroups?.length > 0 && (
                  <>
                    {carriersWithoutSubgroup?.length > 0 && (
                      <Separator variant='dashed' />
                    )}
                    {subgroups.map(
                      ({ groupName, carriers: subgroupCarriers }) => (
                        <div key={groupName} className='flex flex-col gap-2'>
                          <p className='m-0 text-sm font-medium text-gray-700'>
                            {groupName}
                          </p>
                          <SectionCarrierList carriers={subgroupCarriers} />
                        </div>
                      )
                    )}
                  </>
                )}
              </section>
            )
          )}
        </div>
      </div>
    </Modal>
  );
}

UntappedSuggestSummaryModal.propTypes = {
  active: propTypes.bool.isRequired,
  agentName: propTypes.string.isRequired,
  agentEmail: propTypes.string,
  groupedCarriers: propTypes.arrayOf(
    propTypes.shape({
      parentGroupName: propTypes.string.isRequired,
      carriersWithoutSubgroup: propTypes.arrayOf(propTypes.object).isRequired,
      subgroups: propTypes.arrayOf(
        propTypes.shape({
          groupName: propTypes.string.isRequired,
          carriers: propTypes.arrayOf(propTypes.object).isRequired
        })
      )
    })
  ),
  confirmLoading: propTypes.bool,
  onClose: propTypes.func.isRequired,
  onConfirm: propTypes.func.isRequired
};
